import React from 'react';
import styled from 'styled-components';
import { Body5, Body7 } from '@ui';
import { CloseButton, SkeletonButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { TagSearchInput } from '@features/query/form/TagInput';
import { InputStyling } from '@features/query/form/TagInput/styles';
import { ModeContext } from '@/contexts/ModeContext';

const Wrapper = styled.div`
  position: relative;
`;

/*
  Surfaced on componentNoOpacity rather than appBackground: this opens over the detail drawer,
  which is already appBackground, so a matching surface would read as one flat sheet.
*/
const PopoverWrapper = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  width: 320px;
  max-width: calc(100vw - 48px);
  z-index: 10002;

  margin-bottom: 8px;
  padding: 16px;

  display: flex;
  flex-direction: column;
  gap: 12px;

  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  box-shadow: 0 2px 8px 1px rgba(0, 0, 0, 0.14);
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CloseButtonWrapper = styled.div`
  flex-shrink: 0;
  margin: -4px -4px 0 0;

  svg {
    height: 12px;
    width: 12px;
  }
`;

/*
  The shared tag search renders a bare input and expects a caller-supplied frame -- InputStyling
  is that frame, and the results dropdown is positioned against it.
*/
const SearchWrapper = styled.div`
  ${InputStyling} {
    background: ${({ theme }) => theme.themeColors.appBackground};
  }
`;

/*
  Tagging moves a document between the vault's groups, so the same control serves one document
  or a selection -- the caller decides which documents it applies to.
*/
export const TagDocumentsAction = ({ docs, onTag, isSaving, error, label }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [pickError, setPickError] = React.useState(null);
  const inputRef = React.useRef(null);

  /*
    onlyCompanyTags keeps plain companies out of the results: the search returns them with a null
    doc_tag_id, and a document can only be filed under a tag that exists. enableCreateTag is how
    one gets made, so the same search still covers a company that has never been tagged. This is
    the configuration FileDetail's tag field uses.
  */
  const [searchMode] = React.useState({ onlyCompanyTags: true, showCompanyTags: true });

  const count = docs?.length || 0;
  if (count === 0) return null;

  const handleAddTag = async (tag) => {
    const docTagId = tag?.doc_tag_id ?? tag?.id;
    if (!docTagId) {
      // Nothing to PATCH -- say so rather than swallowing the click.
      setPickError('That company has no tag yet. Use “Create” to make one.');
      return;
    }

    setPickError(null);
    const result = await onTag(docs, docTagId);
    if (result?.ok) setIsOpen(false);
  };

  return (
    <Wrapper>
      <SkeletonButton
        disabled={isSaving}
        onClick={
          isSaving
            ? () => {}
            : () => {
                setPickError(null);
                setIsOpen(!isOpen);
              }
        }
      >
        {isSaving ? 'Tagging…' : label || (count > 1 ? `Add tag to ${count}` : 'Add tag')}
      </SkeletonButton>

      {isOpen ? (
        <PopoverWrapper>
          <HeaderWrapper>
            <TitleWrapper>
              <Body5 bold>{count > 1 ? `Tag ${count} documents` : 'Tag document'}</Body5>
              <Body7 isLowContrast>Pick a company or tag to file {count > 1 ? 'them' : 'it'} under.</Body7>
            </TitleWrapper>
            <CloseButtonWrapper>
              <CloseButton onClick={() => setIsOpen(false)} size={12} />
            </CloseButtonWrapper>
          </HeaderWrapper>

          <SearchWrapper>
            <InputStyling>
              <ModeContext.Provider value={searchMode}>
                <TagSearchInput
                  forwardRef={inputRef}
                  onAddTag={handleAddTag}
                  placeholder="Search companies and tags"
                  enableCreateTag
                  showCompanyTags
                />
              </ModeContext.Provider>
            </InputStyling>
          </SearchWrapper>

          {error || pickError ? <ErrorText>{error || pickError}</ErrorText> : null}
        </PopoverWrapper>
      ) : null}
    </Wrapper>
  );
};
